import React, { useState, useEffect, useRef } from 'react';
import {
  Scale, Building2, Wrench, Car, Truck, Phone, Mail, Globe, MapPin, Star,
  Search, Filter, ChevronDown, ChevronUp, ExternalLink, CheckCircle,
  RefreshCw, ArrowLeft, Award, Sparkles, Send, Shield, Briefcase, Clock
} from 'lucide-react';
import { db, listingTiers } from '../lib/supabase';
import { trackDirectoryView, trackDirectoryClick } from '../lib/analytics';
import { TopBanner, BottomBanner } from './BannerAd';

// ==================== SERVICE CATEGORIES ====================
const SERVICE_CATEGORIES = [
  { id: 'all', label: 'All Services', icon: Star, gradient: 'from-gray-600 to-gray-700', bg: 'gray' },
  { id: 'lawyer', label: 'Traffic Lawyers', icon: Scale, gradient: 'from-purple-500 to-purple-600', bg: 'purple' },
  { id: 'insurance', label: 'Insurance', icon: Building2, gradient: 'from-emerald-500 to-emerald-600', bg: 'emerald' },
  { id: 'evaluator', label: 'Vehicle Evaluators', icon: Wrench, gradient: 'from-orange-500 to-orange-600', bg: 'orange' },
  { id: 'mechanic', label: 'Mechanics', icon: Wrench, gradient: 'from-blue-500 to-blue-600', bg: 'blue' },
  { id: 'wrecker', label: 'Wrecker Services', icon: Truck, gradient: 'from-red-500 to-red-600', bg: 'red' },
  { id: 'parts', label: 'Auto Parts', icon: Car, gradient: 'from-cyan-500 to-cyan-600', bg: 'cyan' },
  { id: 'dealership', label: 'Car Dealerships', icon: Briefcase, gradient: 'from-amber-500 to-amber-600', bg: 'amber' }
];

// ==================== PARISHES ====================
const PARISHES = [
  'All Parishes', 'Kingston', 'St. Andrew', 'St. Thomas', 'Portland', 'St. Mary',
  'St. Ann', 'Trelawny', 'St. James', 'Hanover', 'Westmoreland',
  'St. Elizabeth', 'Manchester', 'Clarendon', 'St. Catherine'
];

// ==================== CATEGORY CARD COMPONENT ====================
const CategoryCard = ({ category, isActive, onClick, count }) => {
  const Icon = category.icon;

  return (
    <button
      onClick={onClick}
      className={`relative overflow-hidden rounded-2xl p-4 text-left transition-all transform hover:scale-105 hover:shadow-xl ${
        isActive
          ? `bg-gradient-to-br ${category.gradient} text-white shadow-lg ring-4 ring-white/50`
          : 'bg-white text-gray-700 border-2 border-gray-100 hover:border-gray-200'
      }`}
    >
      {isActive && (
        <div className="absolute inset-0 opacity-20">
          <div className="absolute -top-4 -right-4 w-24 h-24 bg-white rounded-full blur-xl" />
        </div>
      )}
      <div className="relative">
        <div className={`w-12 h-12 rounded-xl flex items-center justify-center mb-3 ${
          isActive ? 'bg-white/20' : `bg-${category.bg}-100`
        }`}>
          <Icon className={`w-6 h-6 ${isActive ? 'text-white' : `text-${category.bg}-600`}`} />
        </div>
        <h3 className={`font-bold text-sm ${isActive ? 'text-white' : 'text-gray-800'}`}>{category.label}</h3>
        {count !== undefined && (
          <p className={`text-xs mt-1 ${isActive ? 'text-white/80' : 'text-gray-500'}`}>{count} providers</p>
        )}
      </div>
    </button>
  );
};

// ==================== PROVIDER CARD COMPONENT ====================
const ProviderCard = ({ provider, catConfig, isExpanded, onToggle, onContactClick, getTierBadge }) => {
  const isPremium = provider.tier === 'premium';
  const isBasic = provider.tier === 'basic';
  const isPaid = isPremium || isBasic;

  // Color configurations for each category
  const colorConfig = {
    lawyer: { header: 'from-purple-500 to-purple-600', light: 'purple-50', text: 'purple-600', badge: 'purple', accent: 'purple' },
    insurance: { header: 'from-emerald-500 to-emerald-600', light: 'emerald-50', text: 'emerald-600', badge: 'emerald', accent: 'emerald' },
    evaluator: { header: 'from-orange-500 to-orange-600', light: 'orange-50', text: 'orange-600', badge: 'orange', accent: 'orange' },
    mechanic: { header: 'from-blue-500 to-blue-600', light: 'blue-50', text: 'blue-600', badge: 'blue', accent: 'blue' },
    wrecker: { header: 'from-red-500 to-red-600', light: 'red-50', text: 'red-600', badge: 'red', accent: 'red' },
    parts: { header: 'from-cyan-500 to-cyan-600', light: 'cyan-50', text: 'cyan-600', badge: 'cyan', accent: 'cyan' },
    dealership: { header: 'from-amber-500 to-amber-600', light: 'amber-50', text: 'amber-600', badge: 'amber', accent: 'amber' }
  };

  const colors = colorConfig[provider.type] || colorConfig.lawyer;

  // Get Google Maps URL
  const getGoogleMapsUrl = () => {
    const address = provider.address || '';
    const parish = provider.parish || '';
    const query = `${address}${address && parish ? ', ' : ''}${parish}${address || parish ? ', Jamaica' : ''}`;
    return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(query)}`;
  };

  return (
    <div className={`bg-white rounded-2xl overflow-hidden transition-all duration-300 ${
      isPremium
        ? 'ring-2 ring-amber-400 shadow-lg shadow-amber-100 hover:shadow-xl hover:shadow-amber-200'
        : isBasic
          ? 'ring-1 ring-blue-200 shadow-md hover:shadow-lg'
          : 'border border-gray-200 shadow-sm hover:shadow-md'
    }`}>

      {/* Premium Header Banner */}
      {isPremium && (
        <div className="bg-gradient-to-r from-amber-400 via-yellow-400 to-amber-400 px-4 py-1.5 flex items-center justify-center gap-2">
          <Sparkles className="w-4 h-4 text-amber-800" />
          <span className="text-xs font-bold text-amber-900 uppercase tracking-wider">Featured Business</span>
          <Sparkles className="w-4 h-4 text-amber-800" />
        </div>
      )}

      {/* Category Color Strip */}
      <div className={`h-1 bg-gradient-to-r ${colors.header}`} />

      {/* Main Card Header - Clickable */}
      <div onClick={onToggle} className="p-4 cursor-pointer hover:bg-gray-50 transition-colors">
        <div className="flex items-start gap-4">
          {/* Logo/Icon */}
          <div className={`relative flex-shrink-0 ${isPremium ? 'w-16 h-16' : 'w-14 h-14'}`}>
            <div className={`w-full h-full bg-gradient-to-br ${colors.header} rounded-xl flex items-center justify-center shadow-lg`}>
              {provider.logo_url || provider.image_url ? (
                <img
                  src={provider.logo_url || provider.image_url}
                  alt={provider.name}
                  className={`${isPremium ? 'w-14 h-14' : 'w-12 h-12'} rounded-lg object-cover`}
                />
              ) : (
                <catConfig.icon className={`${isPremium ? 'w-8 h-8' : 'w-6 h-6'} text-white`} />
              )}
            </div>
            {provider.is_verified && (
              <div className="absolute -bottom-1 -right-1 w-6 h-6 bg-emerald-500 rounded-full flex items-center justify-center border-2 border-white shadow">
                <CheckCircle className="w-4 h-4 text-white" />
              </div>
            )}
          </div>

          {/* Business Info */}
          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <h3 className={`font-bold text-gray-900 leading-tight ${isPremium ? 'text-xl' : 'text-lg'}`}>
                  {provider.name}
                </h3>
                <div className="flex items-center gap-2 mt-1 flex-wrap">
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold bg-${colors.badge}-100 text-${colors.badge}-700`}>
                    {catConfig.label}
                  </span>
                  {isPremium && (
                    <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-bold bg-gradient-to-r from-amber-100 to-yellow-100 text-amber-700 border border-amber-200">
                      <Award className="w-3 h-3" />Premium
                    </span>
                  )}
                  {isBasic && (
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold bg-blue-100 text-blue-700">
                      Basic
                    </span>
                  )}
                  {provider.is_verified && (
                    <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold bg-emerald-100 text-emerald-700">
                      <Shield className="w-3 h-3" />Verified
                    </span>
                  )}
                </div>
              </div>
              <ChevronDown className={`w-6 h-6 text-gray-400 transition-transform duration-300 flex-shrink-0 ${isExpanded ? 'rotate-180' : ''}`} />
            </div>

            {/* Preview Info */}
            {provider.description && (
              <p className="text-gray-600 text-sm mt-2 line-clamp-2">{provider.description}</p>
            )}

            {/* Quick Contact Preview */}
            {!isExpanded && (provider.phone || provider.parish) && (
              <div className="flex items-center gap-4 mt-2 text-sm text-gray-500">
                {provider.phone && (
                  <span className="flex items-center gap-1">
                    <Phone className="w-3.5 h-3.5" />{provider.phone}
                  </span>
                )}
                {(provider.parish || provider.parishes?.length > 0) && (
                  <span className="flex items-center gap-1">
                    <MapPin className="w-3.5 h-3.5" />
                    {provider.parishes?.length > 0 ? provider.parishes[0] : provider.parish}
                  </span>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Expanded Content - Full Business Profile */}
      {isExpanded && (
        <div className="border-t border-gray-100">
          {/* About Section */}
          {provider.description && (
            <div className={`px-5 py-4 ${isPremium ? 'bg-gradient-to-r from-amber-50 to-yellow-50' : isBasic ? 'bg-blue-50' : 'bg-gray-50'}`}>
              <h4 className="text-sm font-bold text-gray-700 uppercase tracking-wide mb-2 flex items-center gap-2">
                <Briefcase className="w-4 h-4" />About This Business
              </h4>
              <p className="text-gray-700 leading-relaxed">{provider.description}</p>
            </div>
          )}

          {/* Contact Information Grid */}
          <div className="px-5 py-4">
            <h4 className="text-sm font-bold text-gray-700 uppercase tracking-wide mb-3 flex items-center gap-2">
              <Phone className="w-4 h-4" />Contact Information
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {/* Contact Person */}
              {provider.contact_name && (
                <div className="bg-gray-50 rounded-xl p-4 border border-gray-100">
                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Contact Person</p>
                  <p className="text-gray-900 font-semibold text-lg">{provider.contact_name}</p>
                </div>
              )}

              {/* Phone */}
              {provider.phone && (
                <div className="bg-emerald-50 rounded-xl p-4 border border-emerald-100 hover:border-emerald-300 transition-colors cursor-pointer group"
                     onClick={(e) => { e.stopPropagation(); onContactClick(provider, 'phone', provider.phone); }}>
                  <p className="text-xs font-semibold text-emerald-600 uppercase tracking-wide mb-1">Phone Number</p>
                  <p className="text-emerald-700 font-bold text-lg flex items-center gap-2 group-hover:text-emerald-800">
                    <Phone className="w-5 h-5" />
                    {provider.phone}
                  </p>
                  <p className="text-xs text-emerald-600 mt-1">Click to call</p>
                </div>
              )}

              {/* Email */}
              {provider.email && (
                <div className="bg-blue-50 rounded-xl p-4 border border-blue-100 hover:border-blue-300 transition-colors cursor-pointer group"
                     onClick={(e) => { e.stopPropagation(); onContactClick(provider, 'email', provider.email); }}>
                  <p className="text-xs font-semibold text-blue-600 uppercase tracking-wide mb-1">Email Address</p>
                  <p className="text-blue-700 font-semibold flex items-center gap-2 group-hover:text-blue-800 break-all">
                    <Mail className="w-5 h-5 flex-shrink-0" />
                    {provider.email}
                  </p>
                  <p className="text-xs text-blue-600 mt-1">Click to send email</p>
                </div>
              )}

              {/* Website */}
              {provider.website && (
                <div className="bg-violet-50 rounded-xl p-4 border border-violet-100 hover:border-violet-300 transition-colors cursor-pointer group"
                     onClick={(e) => { e.stopPropagation(); onContactClick(provider, 'website', provider.website); }}>
                  <p className="text-xs font-semibold text-violet-600 uppercase tracking-wide mb-1">Website</p>
                  <p className="text-violet-700 font-semibold flex items-center gap-2 group-hover:text-violet-800 break-all">
                    <Globe className="w-5 h-5 flex-shrink-0" />
                    {provider.website}
                  </p>
                  <p className="text-xs text-violet-600 mt-1">Click to visit</p>
                </div>
              )}
            </div>
          </div>

          {/* Location Section */}
          {(provider.parish || provider.parishes?.length > 0 || provider.address) && (
            <div className="px-5 py-4 border-t border-gray-100">
              <h4 className="text-sm font-bold text-gray-700 uppercase tracking-wide mb-3 flex items-center gap-2">
                <MapPin className="w-4 h-4" />Location
              </h4>
              <div className="bg-gray-50 rounded-xl p-4 border border-gray-100">
                {/* Parish */}
                {(provider.parish || provider.parishes?.length > 0) && (
                  <div className="mb-3">
                    <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Parish</p>
                    <p className="text-gray-900 font-semibold text-lg flex items-center gap-2">
                      <MapPin className="w-5 h-5 text-gray-400" />
                      {provider.parishes?.length > 0 ? provider.parishes.join(', ') : provider.parish}
                    </p>
                  </div>
                )}

                {/* Address */}
                {provider.address && (
                  <div className={provider.parish || provider.parishes?.length > 0 ? 'pt-3 border-t border-gray-200' : ''}>
                    <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Street Address</p>
                    <p className="text-gray-800 mb-3">{provider.address}</p>
                    <a
                      href={getGoogleMapsUrl()}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={(e) => e.stopPropagation()}
                      className="inline-flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 rounded-lg text-sm font-semibold text-gray-700 hover:bg-gray-50 hover:border-gray-400 transition-all shadow-sm"
                    >
                      <ExternalLink className="w-4 h-4" />
                      View on Google Maps
                    </a>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Services Section */}
          {(provider.services?.length > 0 || provider.specialties?.length > 0) && (
            <div className="px-5 py-4 border-t border-gray-100">
              <h4 className="text-sm font-bold text-gray-700 uppercase tracking-wide mb-3 flex items-center gap-2">
                <Star className="w-4 h-4" />Services Offered
              </h4>
              <div className="flex flex-wrap gap-2">
                {(provider.services || provider.specialties || []).map((service, idx) => (
                  <span
                    key={idx}
                    className={`px-4 py-2 rounded-full text-sm font-medium border ${
                      isPremium
                        ? 'bg-amber-50 border-amber-200 text-amber-800'
                        : `bg-${colors.badge}-50 border-${colors.badge}-200 text-${colors.badge}-700`
                    }`}
                  >
                    {typeof service === 'object' ? (service.label || service.value || '') : service}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Call to Action Buttons */}
          <div className={`px-5 py-4 border-t ${isPremium ? 'bg-gradient-to-r from-amber-50 to-yellow-50' : 'bg-gray-50'}`}>
            <div className="flex flex-wrap gap-3">
              {provider.phone && (
                <button
                  onClick={(e) => { e.stopPropagation(); onContactClick(provider, 'phone', provider.phone); }}
                  className="flex-1 min-w-[140px] flex items-center justify-center gap-2 px-5 py-3 bg-gradient-to-r from-emerald-500 to-emerald-600 text-white rounded-xl font-bold hover:from-emerald-600 hover:to-emerald-700 shadow-lg shadow-emerald-200 transition-all"
                >
                  <Phone className="w-5 h-5" />Call Now
                </button>
              )}
              {provider.email && (
                <button
                  onClick={(e) => { e.stopPropagation(); onContactClick(provider, 'email', provider.email); }}
                  className="flex-1 min-w-[140px] flex items-center justify-center gap-2 px-5 py-3 bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-xl font-bold hover:from-blue-600 hover:to-blue-700 shadow-lg shadow-blue-200 transition-all"
                >
                  <Mail className="w-5 h-5" />Send Email
                </button>
              )}
              {provider.website && (
                <button
                  onClick={(e) => { e.stopPropagation(); onContactClick(provider, 'website', provider.website); }}
                  className="flex-1 min-w-[140px] flex items-center justify-center gap-2 px-5 py-3 bg-gradient-to-r from-violet-500 to-violet-600 text-white rounded-xl font-bold hover:from-violet-600 hover:to-violet-700 shadow-lg shadow-violet-200 transition-all"
                >
                  <Globe className="w-5 h-5" />Visit Website
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ==================== SERVICE PROVIDERS PAGE ====================
export const ServiceProvidersPage = ({ setPage, goBack, lang }) => {
  const [category, setCategory] = useState('all');
  const [parish, setParish] = useState('All Parishes');
  const [search, setSearch] = useState('');
  const [providers, setProviders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState(null);
  const [tiers, setTiers] = useState([]);
  const resultsRef = useRef(null);

  useEffect(() => {
    loadTiers();
  }, []);

  useEffect(() => {
    loadProviders();
  }, [category]);

  const loadTiers = async () => {
    try {
      const { data, error } = await listingTiers.getAll();
      if (error) console.error('Error loading tiers:', error);
      setTiers(data || []);
    } catch (error) {
      console.error('Error loading tiers:', error);
    }
  };

  const loadProviders = async () => {
    setLoading(true);

    // Safety timeout - ensure loading stops after 10 seconds max
    const timeoutId = setTimeout(() => {
      console.warn('Provider loading timeout - stopping spinner');
      setLoading(false);
    }, 10000);

    try {
      let allProviders = [];

      // All provider types are now in the unified service_providers table
      if (category === 'all') {
        // Load all service providers
        const { data, error } = await db.serviceProviders.getAll();
        if (error) console.error('Error loading service providers:', error);
        allProviders = data || [];
      } else {
        // Load specific type from service_providers
        const { data, error } = await db.serviceProviders.getByType(category);
        if (error) console.error(`Error loading ${category}:`, error);
        allProviders = data || [];
      }

      // Sort: premium first, then verified, then alphabetically
      allProviders.sort((a, b) => {
        // Premium tier first, then basic, then free
        const tierOrder = { premium: 0, basic: 1, free: 2 };
        const tierA = tierOrder[a.tier] ?? 2;
        const tierB = tierOrder[b.tier] ?? 2;
        if (tierA !== tierB) return tierA - tierB;

        // Then verified
        if (a.is_verified !== b.is_verified) return b.is_verified ? 1 : -1;

        // Then alphabetically
        return (a.name || '').localeCompare(b.name || '');
      });

      setProviders(allProviders);
    } catch (error) {
      console.error('Error loading providers:', error);
    } finally {
      clearTimeout(timeoutId);
      setLoading(false);
    }
  };

  // Filter providers
  const filteredProviders = providers.filter(p => {
    // Category filter (already applied in loadProviders for single category)
    if (category !== 'all' && p.type !== category) return false;

    // Parish filter
    if (parish !== 'All Parishes') {
      const parishes = p.parishes || (p.parish ? [p.parish] : []);
      if (!parishes.some(pp => pp.toLowerCase().includes(parish.toLowerCase()))) return false;
    }

    // Search filter
    if (search) {
      const searchLower = search.toLowerCase();
      return (
        p.name?.toLowerCase().includes(searchLower) ||
        p.firm?.toLowerCase().includes(searchLower) ||
        p.description?.toLowerCase().includes(searchLower) ||
        (p.services || []).some(s => s.toLowerCase().includes(searchLower))
      );
    }

    return true;
  });

  // Get counts per category
  const getCategoryCount = (catId) => {
    if (catId === 'all') return providers.length;
    return providers.filter(p => p.type === catId).length;
  };

  const handleProviderClick = (provider) => {
    trackDirectoryView(provider.type, provider.id, provider.name, provider.parish);
    setExpandedId(expandedId === provider.id ? null : provider.id);
  };

  const handleContactClick = (provider, type, value) => {
    trackDirectoryClick(provider.type, provider.id, provider.name, type, provider.parish);
    if (type === 'phone') window.location.href = `tel:${value}`;
    else if (type === 'email') window.location.href = `mailto:${value}`;
    else if (type === 'website') window.open(value, '_blank');
  };

  const getCategoryConfig = (type) => {
    return SERVICE_CATEGORIES.find(c => c.id === type) || SERVICE_CATEGORIES[0];
  };

  const handleCategorySelect = (catId) => {
    setCategory(catId);
    // Scroll to results after a short delay to allow state update
    setTimeout(() => {
      resultsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 100);
  };

  const getTierBadge = (tier) => {
    switch (tier) {
      case 'premium':
        return <span className="px-2.5 py-1 bg-gradient-to-r from-amber-400 to-amber-500 text-white text-xs font-bold rounded-full flex items-center gap-1 shadow-sm"><Award className="w-3 h-3" />Premium</span>;
      case 'basic':
        return <span className="px-2.5 py-1 bg-blue-100 text-blue-700 text-xs font-bold rounded-full">Basic</span>;
      default:
        return null;
    }
  };

  return (
    <div className="space-y-6">
      {/* Back Button */}
      <button onClick={() => goBack ? goBack() : setPage('home')}
        className="flex items-center gap-2 text-gray-600 hover:text-gray-900 font-medium">
        <ArrowLeft className="w-5 h-5" />Back
      </button>

      {/* Top Banner Ad */}
      <TopBanner page="services" />

      {/* Hero Header */}
      <section className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-emerald-800 via-emerald-700 to-teal-600 text-white p-8 shadow-2xl">
        <div className="absolute inset-0 opacity-20">
          <div className="absolute top-0 right-0 w-64 h-64 bg-yellow-400 rounded-full blur-3xl" />
          <div className="absolute bottom-0 left-0 w-48 h-48 bg-teal-300 rounded-full blur-3xl" />
        </div>
        <div className="relative z-10">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-14 h-14 bg-white/20 rounded-2xl flex items-center justify-center">
              <Building2 className="w-8 h-8" />
            </div>
            <div>
              <h2 className="text-3xl font-black">Service Providers</h2>
              <p className="text-emerald-200">Find trusted professionals across Jamaica</p>
            </div>
          </div>
          <p className="text-emerald-100 max-w-2xl">
            Browse our directory of verified traffic lawyers, insurance companies, mechanics, and more.
            All providers are vetted to ensure quality service for Jamaican motorists.
          </p>
        </div>
      </section>

      {/* Category Grid */}
      <section>
        <h3 className="font-bold text-gray-800 mb-4 flex items-center gap-2">
          <Filter className="w-5 h-5" /> Browse by Category
        </h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {SERVICE_CATEGORIES.map(cat => (
            <CategoryCard
              key={cat.id}
              category={cat}
              isActive={category === cat.id}
              onClick={() => handleCategorySelect(cat.id)}
              count={getCategoryCount(cat.id)}
            />
          ))}
        </div>
      </section>

      {/* Search & Filters */}
      <section className="bg-white rounded-2xl p-5 shadow-lg border border-gray-100">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by name, services, specialties..."
              className="w-full pl-12 pr-4 py-3 border-2 border-gray-200 rounded-xl focus:border-emerald-500 focus:ring-4 focus:ring-emerald-100 focus:outline-none transition-all"
            />
          </div>
          <div className="relative">
            <MapPin className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-emerald-500" />
            <select
              value={parish}
              onChange={(e) => setParish(e.target.value)}
              className="pl-12 pr-10 py-3 border-2 border-gray-200 rounded-xl focus:border-emerald-500 focus:ring-4 focus:ring-emerald-100 focus:outline-none appearance-none bg-white font-medium"
            >
              {PARISHES.map(p => <option key={p} value={p}>{p}</option>)}
            </select>
            <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none" />
          </div>
        </div>
      </section>

      {/* Results Header */}
      <div ref={resultsRef} className="flex items-center justify-between scroll-mt-4">
        <div className="flex items-center gap-3">
          <span className="px-4 py-2 bg-emerald-100 text-emerald-700 rounded-xl font-bold">
            {filteredProviders.length} {filteredProviders.length === 1 ? 'Provider' : 'Providers'}
          </span>
          {category !== 'all' && (
            <span className={`px-3 py-1.5 bg-gradient-to-r ${getCategoryConfig(category).gradient} text-white rounded-lg text-sm font-medium`}>
              {getCategoryConfig(category).label}
            </span>
          )}
        </div>
        {parish !== 'All Parishes' && (
          <span className="text-sm text-gray-500 flex items-center gap-1">
            <MapPin className="w-4 h-4" /> {parish}
          </span>
        )}
      </div>

      {/* Providers List */}
      {loading ? (
        <div className="flex flex-col items-center justify-center py-16 bg-white rounded-2xl">
          <RefreshCw className="w-12 h-12 text-emerald-500 animate-spin mb-4" />
          <p className="text-gray-500 font-medium">Loading providers...</p>
        </div>
      ) : filteredProviders.length === 0 ? (
        <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-2xl p-12 text-center">
          <div className="w-20 h-20 bg-gray-200 rounded-full flex items-center justify-center mx-auto mb-4">
            <Building2 className="w-10 h-10 text-gray-400" />
          </div>
          <h3 className="text-xl font-bold text-gray-700 mb-2">No Providers Found</h3>
          <p className="text-gray-500 mb-6">Try adjusting your filters or search term</p>
          <button onClick={() => setPage('getlisted')} className="px-6 py-3 bg-gradient-to-r from-emerald-500 to-emerald-600 text-white font-bold rounded-xl hover:from-emerald-600 hover:to-emerald-700 shadow-lg shadow-emerald-200">
            <Send className="w-5 h-5 inline mr-2" />Get Your Business Listed
          </button>
        </div>
      ) : (
        <div className="space-y-4">
          {filteredProviders.map((provider) => (
            <ProviderCard
              key={provider.id}
              provider={provider}
              catConfig={getCategoryConfig(provider.type)}
              isExpanded={expandedId === provider.id}
              onToggle={() => handleProviderClick(provider)}
              onContactClick={handleContactClick}
              getTierBadge={getTierBadge}
            />
          ))}
        </div>
      )}

      {/* Get Listed CTA */}
      <section className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-violet-600 via-purple-600 to-indigo-700 text-white p-8 text-center shadow-2xl">
        <div className="absolute inset-0 opacity-20">
          <div className="absolute top-0 left-1/2 w-64 h-64 bg-pink-400 rounded-full blur-3xl" />
        </div>
        <div className="relative z-10">
          <div className="w-16 h-16 bg-white/20 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <Briefcase className="w-8 h-8" />
          </div>
          <h3 className="text-2xl font-black mb-2">Are you a service provider?</h3>
          <p className="text-violet-200 mb-6 max-w-lg mx-auto">
            Get listed in our directory and reach thousands of Jamaican motorists looking for trusted services
          </p>
          <button
            onClick={() => setPage('getlisted')}
            className="px-8 py-4 bg-white text-violet-600 font-black rounded-xl hover:bg-violet-50 shadow-xl transition-all transform hover:scale-105"
          >
            <Send className="w-5 h-5 inline mr-2" />Get Listed Today
          </button>
        </div>
      </section>

      {/* Bottom Banner Ad */}
      <BottomBanner page="services" />
    </div>
  );
};

export default ServiceProvidersPage;
